import { useLayoutEffect, useRef, type ReactNode } from 'react';

import { useDialogLayer } from './dialogLayer.js';

/** The custom property the toast viewport reads so a toast never lands on a dialog's actions. */
export const DIALOG_FOOT_PROPERTY = '--pbx-dialog-foot';

/**
 * @internal The foot of a `DialogFrame`: the footer, then the dialog-layer slot. Registers the
 * dialog with the layer provider for exactly as long as the content is mounted, and — while
 * this dialog is the top-most — publishes the distance from the viewport's bottom edge to the
 * top of this foot on `<html>`, so `Toast.tsx` can stack above it. A review capture
 * (2026-10-03, r3) had a toast sitting on a drawer's Save button with the body's pointer
 * events off: unreadable and un-dismissable, over the one control the person wanted.
 */
export function DialogFoot({ footer, layerSlot }: { footer: ReactNode; layerSlot: boolean }): ReactNode {
  const { slot, isTop } = useDialogLayer();
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const foot = ref.current;
    const root = document.documentElement;
    if (!isTop || !foot) return undefined;
    const publish = (): void => {
      const top = foot.getBoundingClientRect().top;
      root.style.setProperty(DIALOG_FOOT_PROPERTY, `${Math.max(0, Math.round(window.innerHeight - top))}px`);
    };
    publish();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(publish) : null;
    observer?.observe(foot);
    window.addEventListener('resize', publish);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', publish);
      root.style.removeProperty(DIALOG_FOOT_PROPERTY);
    };
  }, [isTop]);

  return (
    <div ref={ref} data-dialog-foot>
      {footer ? (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border px-5 py-4">{footer}</div>
      ) : null}
      {layerSlot ? slot : null}
    </div>
  );
}
